/**
 * ConfirmDialog - Reusable confirmation dialog component
 * Replaces browser confirm() dialogs with styled modal
 */

import React from 'react';
import { AnimatedModal } from './AnimatedModal';

export type ConfirmDialogType = 'danger' | 'warning' | 'info' | 'success';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  type?: ConfirmDialogType;
  isLoading?: boolean;
}

const typeStyles = {
  danger: {
    icon: 'fa-exclamation-triangle',
    iconColor: 'text-red-500',
    iconBg: 'bg-red-50',
    buttonBg: 'bg-red-600 hover:bg-red-700',
  },
  warning: {
    icon: 'fa-exclamation-circle',
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-50',
    buttonBg: 'bg-amber-600 hover:bg-amber-700',
  },
  info: {
    icon: 'fa-info-circle',
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-50',
    buttonBg: 'bg-blue-600 hover:bg-blue-700',
  },
  success: {
    icon: 'fa-check-circle',
    iconColor: 'text-green-500',
    iconBg: 'bg-green-50',
    buttonBg: 'bg-green-600 hover:bg-green-700',
  },
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  type = 'warning',
  isLoading = false,
}) => {
  const styles = typeStyles[type];

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <AnimatedModal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="p-6">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`flex-shrink-0 w-10 h-10 rounded-full ${styles.iconBg} flex items-center justify-center`}>
            <i className={`fas ${styles.icon} ${styles.iconColor} text-lg`} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <div className="mt-2 text-sm text-gray-600">
              {typeof message === 'string' ? <p>{message}</p> : message}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${styles.buttonBg}`}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <i className="fas fa-spinner fa-spin" />
                Processing...
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
};

/**
 * Hook for managing confirm dialog state
 */
interface UseConfirmDialogOptions {
  title: string;
  message: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  type?: ConfirmDialogType;
  onConfirm: () => void | Promise<void>;
}

interface ConfirmDialogState {
  isOpen: boolean;
  isLoading: boolean;
  options: UseConfirmDialogOptions | null;
}

export const useConfirmDialog = () => {
  const [state, setState] = React.useState<ConfirmDialogState>({
    isOpen: false,
    isLoading: false,
    options: null,
  });

  const confirm = React.useCallback((options: UseConfirmDialogOptions) => {
    setState({
      isOpen: true,
      isLoading: false,
      options,
    });
  }, []);

  const handleClose = React.useCallback(() => {
    setState(prev => ({
      ...prev,
      isOpen: false,
      isLoading: false,
    }));
  }, []);

  const handleConfirm = React.useCallback(async () => {
    if (!state.options) return;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      await state.options.onConfirm();
      handleClose();
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [state.options, handleClose]);

  const dialogProps = React.useMemo(() => ({
    isOpen: state.isOpen,
    isLoading: state.isLoading,
    onClose: handleClose,
    onConfirm: handleConfirm,
    title: state.options?.title || '',
    message: state.options?.message || '',
    confirmLabel: state.options?.confirmLabel,
    cancelLabel: state.options?.cancelLabel,
    type: state.options?.type,
  }), [state, handleClose, handleConfirm]);

  return {
    confirm,
    dialogProps,
    ConfirmDialog,
  };
};

export default ConfirmDialog;
